<template>
  <v-layout wrap>
    <gat-select
      :label="harbourSelectProps?.label || 'Harbour filter'"
      v-model="harbourId"
      :items="harbours"
      textFieldName="NAME"
      codeFieldName="ID"
      :size="harbourSize || harbourSelectProps?.size || 'xs6 sm4'"
      :clearable="!readonly"
      :readonly="readonly" />
    <gat-select
      :label="terminalSelectProps?.label || 'Terminal filter'"
      v-model="terminalName"
      :items="terminals"
      textFieldName="NAME"
      codeFieldName="ID"
      :size="terminalSize || terminalSelectProps?.size || 'xs6 sm4'"
      :clearable="!readonly"
      :readonly="readonly" />
    <gat-lookup-edit
      v-show="useLookup"
      :label="quaySelectProps?.label || label || 'Quay'"
      v-model="quayId"
      :searchresult="items"
      @getItems="getItems($event)"
      textFieldName="NAME"
      codeFieldName="ID"
      @input="updateValue()"
      @itemNotFound="itemNotFound"
      :cashSearches="true"
      :size="size"
      :required="quayRequired"
      :readonly="readonly">
    </gat-lookup-edit>
    <gat-select
      v-show="!useLookup"
      :label="quaySelectProps?.label || label || 'Quay'"
      v-model="quayId"
      :items="quays"
      textFieldName="NAME"
      codeFieldName="ID"
      :size="quaySelectProps?.size || size"
      @input="updateValue()"
      :required="quayRequired"
      customSelection
      :readonly="readonly">
      <template v-slot:selection="{ data }">
        <gat-icon v-if="warningMessage" :tooltip="warningMessage" />
        <span>
          {{ data['NAME'] }}
        </span>
      </template>
    </gat-select>
    <!-- <record-presenter caption="Items" :value="items"></record-presenter>
    <record-presenter caption="quays" :value="quays"></record-presenter>
    <record-presenter caption="terminals" :value="this.helperStore.ownTerminals"></record-presenter>
    <div>Selected HARBOUR ID: {{ harbourId }}</div>
    <div>Selected TERMINAL NAME: {{ terminalName }}</div>
    <div>Selected QUAY ID: {{ quayId }}</div> -->
  </v-layout>
</template>

<script setup lang="ts">
import { apiCashedGet, apiPost } from '@/store/api';
import { useHelperStore } from '@/store/helperStore';
import { usePortcallStore } from '@/store/portcallStore';
import { ref, onMounted, computed, watch } from 'vue';
import GatIcon from './GatIcon.vue';

interface QuaySelectProps {
  harbourSize?: string;
  terminalSize?: string;
  size?: string;
  /** The Quay ID */
  value?: string | number;
  label?: string;
  quayRequired?: boolean;
  showWarningIcon?: boolean;
  warningMessage?: string;
  harbourSelectProps?: {
    label?: string;
    size?: string;
  };
  terminalSelectProps?: {
    label?: string;
    size?: string;
  };
  quaySelectProps?: {
    label?: string;
    size?: string;
  };
  readonly?: boolean;
}

const props = defineProps<QuaySelectProps>();
const emit = defineEmits(['input', 'harbour-changed', 'terminal-changed', 'quay-changed']);

const helperStore = useHelperStore();
const portcallStore = usePortcallStore();

const harbourId = ref<string | number | undefined>();

watch(
  () => harbourId.value,
  (val) => {
    emit('harbour-changed', val);
  }
);

const terminalName = ref<string | number | undefined>();
watch(
  () => terminalName.value,
  (val) => {
    emit('terminal-changed', val);
  }
);
const quayId = ref<string | number | undefined>();
watch(
  () => quayId.value,
  (val) => {
    emit('quay-changed', val);
  }
);
const items = ref<any[]>([]);
const quays = ref<any[]>([]);
// const showfilter = ref(false);
const pauseValueWatchers = ref(false);

function filterInactiveItems(apiResult: any[]) {
  const result = apiResult.filter((item) => item.INACTIVE === 0 || item.ID === props.value);
  return result.map((n) => ({ ...n, NAME: n.INACTIVE === 1 ? `${n.NAME} (inacitve)` : n.NAME }));
}

function getQuays(harbour_id: string | number, terminal_name: string | number) {
  return new Promise<void>((resolve) => {
    if (harbour_id) {
      apiCashedGet(`helper/quay/harbourId/${harbour_id}`).then((data) => {
        let result = [];
        if (terminal_name) {
          result = data.filter((item: any) => item.TERMINAL_NAME == terminal_name);
        } else {
          result = data;
        }
        quays.value = filterInactiveItems(result);
        resolve();
      });
    } else {
      quays.value = [];
      resolve();
    }
  });
}

onMounted(() => {
  if (props.value) {
    apiCashedGet(`helper/quay/id/${props.value}`).then((data) => {
      pauseValueWatchers.value = true;
      harbourId.value = data[0].HARBOUR_ID;
      terminalName.value = data[0].TERMINAL_NAME;
      getQuays(data[0].HARBOUR_ID, data[0].TERMINAL_NAME).then(() => {
        quayId.value = data[0].ID;
        pauseValueWatchers.value = false;
      });
    });
  }
});

const harbours = computed(() => {
  const berthIds = portcallStore.berths.map((b) => b.HARBOUR_ID);
  return helperStore.ownHarbours
    .filter((hb) => hb.INACTIVE !== 1 || berthIds.includes(hb.ID))
    .map((item) => ({
      ...item,
      NAME: item.INACTIVE !== 1 ? item.NAME : `${item.NAME} (inactive)`,
    }));
});

const terminals = computed(() => {
  let result = helperStore.ownTerminals;
  if (harbourId.value) {
    result = helperStore.ownTerminals.filter((item) => item.HARBOUR_ID == harbourId.value);
  }
  const isProbablyInactive = terminalName.value && !result.find((item) => item.ID === terminalName.value);
  if (isProbablyInactive) {
    result.push({
      NAME: `${terminalName.value} (inactive)`,
      ID: terminalName.value,
      HARBOUR_ID: harbourId.value || 0,
    });
  }
  return result;
});

const useLookup = computed(() => {
  let result = true;
  if (harbourId.value || terminalName.value) {
    result = false;
  }
  return result;
});

watch(
  () => props.value,
  (val) => {
    if (!val) {
      harbourId.value = undefined;
      terminalName.value = undefined;
      quayId.value = undefined;
    }
    if (!pauseValueWatchers.value && val) {
      apiCashedGet(`helper/quay/id/${val}`).then((data) => {
        pauseValueWatchers.value = true;
        harbourId.value = data[0].HARBOUR_ID;
        terminalName.value = data[0].TERMINAL_NAME;
        getQuays(data[0].HARBOUR_ID, data[0].TERMINAL_NAME).then(() => {
          quayId.value = data[0].ID;
          pauseValueWatchers.value = false;
        });
      });
    }
  }
);

watch(
  () => harbourId.value,
  (val, oldValue) => {
    if (!pauseValueWatchers.value) {
      if (oldValue && !val) {
        terminalName.value = undefined;
      }
      if (val && terminalName.value) {
        getQuays(val, terminalName.value);
      }
    }
  }
);

function getHarbourIdFromTerminalName(name: string) {
  const terminal = helperStore.ownTerminals.find((item) => item.NAME == name);
  if (terminal) {
    return terminal.HARBOUR_ID;
  }
  return undefined;
}

watch(
  () => terminalName.value,
  (val) => {
    if (!val) {
      return;
    }
    if (!pauseValueWatchers.value) {
      if (!harbourId.value) {
        harbourId.value = getHarbourIdFromTerminalName(val.toString());
      } else {
        getQuays(harbourId.value, val);
      }
    }
  }
);

function getItems(searchText: string) {
  apiPost('helper/search/', { table: 'quay', text: searchText }).then((data: any[]) => {
    const result = data.filter((item) => {
      let include = true;
      if (harbourId.value) {
        include = true;
        // eslint-disable-next-line no-param-reassign
        item.HARBOUR_ID = harbourId.value;
      }
      if (terminalName.value) {
        include = include && item.TERMINAL_NAME == terminalName.value;
      }
      return include;
    });
    items.value = filterInactiveItems(result);
  });
}

function updateValue() {
  emit('input', quayId.value);
}

function itemNotFound(id: any) {
  apiCashedGet(`helper/quay/id/${id}`).then((data) => {
    items.value = filterInactiveItems(data);
  });
}
</script>
